import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import axios from "axios";
import Cookies from "js-cookie";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { FormProvider } from "src/components/hook-form/FormProvider";
import { RHFTextField } from "src/components/hook-form/RHFTextField";
import { NewTabLink } from "src/components/NewTabLink";
import { errorToast } from "src/components_with_stories/toast/toast";
import useIsLightMode from "src/hooks/useIsLightMode";
import { useNavigateAuthenticatedUserToHomePage } from "src/hooks/useNavigateAuthenticatedUserToHomePage";
import RoutedButton from "src/newComponents/buttons/RoutedButton";
import EmailField from "src/newComponents/form/EmailField";
import PasswordField from "src/newComponents/form/PasswordField";

import { createPasswordChangeErrorMessage } from "../authUtils";
import { Icon } from "../components/Icon";

const SignUpForm = () => {
  useNavigateAuthenticatedUserToHomePage();

  const navigate = useNavigate();

  const search = window.location.href.split("?")[1];

  const isLightMode = useIsLightMode();

  const SignUpSchema = Yup.object().shape({
    name: Yup.string().required("Name is required.").min(2, "Your name must be at least 2 characters."),
    email: Yup.string().required("Email is required.").email("Invalid email address."),
    password: Yup.string().required("Password is required.").min(8, "Your password must be at least 8 characters."),
    password_confirmation: Yup.string()
      .required("Confirm password is required.")
      .min(8, "Your password must be at least 8 characters.")
      .oneOf([Yup.ref("password")], "Your passwords do not match."),
  });

  const defaultValues = {
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
  };

  const methods = useForm({
    resolver: yupResolver(SignUpSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, isSubmitSuccessful },
  } = methods;

  // TODO: migrate to userRepository
  const onSubmit = async ({
    name,
    email,
    password,
    password_confirmation,
  }: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
  }) => {
    // clear sessionid and csrftoken cookies
    Cookies.remove("sessionid");
    Cookies.remove("csrftoken");

    axios
      .post(`/api/auth/register/`, {
        email: email,
        name: name,

        password: password,
        password2: password_confirmation,
      })
      .then((response) => {
        axios.defaults.headers.common["X-CSRFToken"] = Cookies.get("csrftoken");

        if (response.status === 201) {
          reset();
          const nextPage = search ? search : "";
          navigate("/verify/?" + nextPage, {
            state: { email: email },
          });
          return;
        }
      })
      .catch((error) => {
        const passwordErrors = error?.response?.data?.password;
        if (passwordErrors !== undefined) {
          const errorMessage = createPasswordChangeErrorMessage(passwordErrors);
          errorToast(errorMessage, isLightMode);
          reset(undefined, { keepValues: true });
          return;
        }

        const emailErrors = error?.response?.data?.email;
        if (emailErrors !== undefined) {
          const errorsExist = Array.isArray(emailErrors) && emailErrors.length > 0;
          if (errorsExist) {
            const firstError = emailErrors[0];

            const message = firstError.includes("User with this email address already exists.")
              ? "You've already got an account, select 'Sign In' now to login"
              : firstError;

            errorToast(message, isLightMode);
            reset(undefined, { keepValues: true });
            return;
          }
        }

        errorToast("Please try again.", isLightMode);
        reset();
      });
  };

  return (
    <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Icon />

      <Typography variant="body2">trusted by thousands of future Pharmacists across the UK</Typography>

      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
        Sign up now to join Pharmacy professionals, from students, Trainees and Pharmacists. Access Foundation
        Pharmacist Training and GPhC exam revision content today.
      </Typography>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2.5} alignItems="flex-end">
          <RHFTextField name="name" label="Name" hasFocus={true} tabIndex={1} />
          <EmailField hasFocus={false} tabIndex={2} />
          <PasswordField name="password" label="Password" hasFocus={false} tabIndex1={3} tabIndex2={6} />
          <PasswordField
            name="password_confirmation"
            label="Confirm Password"
            hasFocus={false}
            tabIndex1={4}
            tabIndex2={7}
          />

          <LoadingButton
            fullWidth
            color="inherit"
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting || isSubmitSuccessful}
            tabIndex={5}
          >
            Join Us - It's FREE
          </LoadingButton>
        </Stack>
      </FormProvider>

      <Typography variant="body2">
        I agree to the <NewTabLink href="/terms">Terms of service</NewTabLink> and{" "}
        <NewTabLink href="/privacy">Privacy policy</NewTabLink>.
      </Typography>

      <Typography variant="body2">
        Already have an account?{" "}
        <RoutedButton path="/login/" buttonColor="inherit" size="medium" variant="text">
          Sign in
        </RoutedButton>
      </Typography>
    </div>
  );
};

export { SignUpForm };
